/**
 * Public-facing news data layer.
 *
 * Reads PUBLISHED articles only from the FastAPI backend.
 * Normalizes the API shape into the field names the existing
 * News UI components already expect (title/tag/date/excerpt
 * for NewsPage, and pullQuote and readMinutes for NewsArticlePage).
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AR_MONTHS = [
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
];
const EN_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function parseISODate(value) {
  if (!value) return null;
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return { y: Number(m[1]), mo: Number(m[2]), d: Number(m[3]) };
}

function formatArabicDate(value) {
  const p = parseISODate(value);
  if (!p) return value || '';
  return `${String(p.d).padStart(2, '0')} ${AR_MONTHS[p.mo - 1]} ${p.y}`;
}

function formatEnglishDate(value) {
  const p = parseISODate(value);
  if (!p) return value || '';
  return `${EN_MONTHS[p.mo - 1]} ${p.d}, ${p.y}`;
}

/**
 * Make an API media URL absolute. Pass-through for http(s):// URLs.
 */
function absoluteUrl(url) {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  if (url.startsWith('/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}

/**
 * Normalize a backend article into the rich shape the public UI uses.
 */
export function normalizeArticle(raw, language = 'ar') {
  if (!raw) return null;
  const dateIso = raw.publish_date || (raw.created_at || '').slice(0, 10);
  const isAr = language === 'ar';
  return {
    ...raw,
    // Image URLs absolute
    image: absoluteUrl(raw.image),
    gallery: Array.isArray(raw.gallery) ? raw.gallery.map(absoluteUrl) : [],

    // Backwards-compatible fields the existing components read
    title: isAr ? (raw.title_ar || raw.title_en || '') : (raw.title_en || raw.title_ar || ''),
    excerpt: isAr ? (raw.excerpt_ar || raw.excerpt_en || '') : (raw.excerpt_en || raw.excerpt_ar || ''),
    tag: isAr ? (raw.tag_ar || raw.tag_en || '') : (raw.tag_en || raw.tag_ar || ''),
    date: isAr ? formatArabicDate(dateIso) : formatEnglishDate(dateIso),

    // Bilingual formatted dates (used by NewsArticlePage `r.date_ar` / `r.date_en`)
    date_ar: formatArabicDate(dateIso),
    date_en: formatEnglishDate(dateIso),

    // Field name aliases used by NewsArticlePage
    pullQuote_ar: raw.pull_quote_ar || '',
    pullQuote_en: raw.pull_quote_en || '',
    readMinutes: raw.read_minutes || 5,
  };
}

export async function fetchPublicArticles(language = 'ar') {
  const { data } = await axios.get(`${API}/news`, { params: { limit: 200 } });
  return data.map((a) => normalizeArticle(a, language));
}

export async function fetchPublicArticle(id, language = 'ar') {
  const { data } = await axios.get(`${API}/news/${id}`);
  return normalizeArticle(data, language);
}
